import { RefObject } from 'react';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CollisionDetection, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { BoardColumnWithLeads } from '../../services/responses/board-column.response';
import { BoardDetailResponse } from '../../services/responses/board.response';
import { LeadResponse } from '../../services/responses/lead.response';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { ScheduleFilter } from '../../filters/crm-schedule-filter';
import { CrmColumn } from './crm-column';
import { CrmLeadCard } from './crm-lead-card';
import { CrmDropActions } from './crm-drop-actions';
import { getCrmColors } from '../../crm-theme';
import { useCrmDrag } from '../../hooks/use-crm-drag.hook';
import { addColumnButtonSx, columnsRowSx } from './crm-board-columns.styles';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';

type DragApi = ReturnType<typeof useCrmDrag>;

interface Props {
    board: BoardDetailResponse;
    rowRef: RefObject<HTMLDivElement>;
    columnsMaxHeight: number | null;
    scrollbarHeight: number;
    searchTerm: string;
    statusFilterId: string | null;
    scheduleFilter: ScheduleFilter | null;
    ownerFilter: string | null;
    tagFilter: string | null;
    statuses: LeadStatusResponse[];
    users?: UserResponse[];
    onAssignLead?: (lead: LeadResponse, userId: string | null) => void;
    sensors: DragApi['sensors'];
    collisionDetection: CollisionDetection;
    activeLead: LeadResponse | null;
    activeColumn: BoardColumnWithLeads | null;
    onDragStart: DragApi['handleDragStart'];
    onDragOver: DragApi['handleDragOver'];
    onDragEnd: DragApi['handleDragEnd'];
    onDragCancel: DragApi['handleDragCancel'];
    preview: DragApi['preview'];
    onOpenLead: (lead: LeadResponse) => void;
    onChangeLeadStatus: (lead: LeadResponse, statusId: string) => void;
    onOpenSchedule: (lead: LeadResponse) => void;
    onAddLead: (columnId: string) => void;
    onEditColumn: (column: BoardColumnWithLeads) => void;
    onDeleteColumn: (columnId: string) => void;
    onSetEntryColumn: (columnId: string) => void;
    focusedLead?: string | null;
    /// Coluna que o painel pediu para mostrar.
    focusedColumn?: string | null;
    onAddColumn: () => void;
    /// Existe outro quadro ativo para onde levar o lead.
    canMoveToBoard: boolean;
}

export function CrmBoardColumns({
    board, rowRef, columnsMaxHeight, scrollbarHeight, searchTerm, statusFilterId, scheduleFilter, ownerFilter, tagFilter, statuses,
    users, onAssignLead,
    sensors, collisionDetection, activeLead, activeColumn,
    onDragStart, onDragOver, onDragEnd, onDragCancel, preview,
    onOpenLead, onChangeLeadStatus, onOpenSchedule, onAddLead, onEditColumn, onDeleteColumn,
    onSetEntryColumn, onAddColumn,
    focusedLead, focusedColumn, canMoveToBoard,
}: Props) {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { canManageBoards } = useProfilePermissions();

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
        >
            <Box ref={rowRef} sx={columnsRowSx(columnsMaxHeight, scrollbarHeight)}>
                <SortableContext
                    items={board.columns.map((c) => c.id)}
                    strategy={horizontalListSortingStrategy}
                >
                    {board.columns.map((col) => (
                        <CrmColumn
                            key={col.id}
                            column={col}
                            searchTerm={searchTerm}
                            statusFilterId={statusFilterId}
                            scheduleFilter={scheduleFilter}
                            ownerFilter={ownerFilter}
                            tagFilter={tagFilter}
                            focused={focusedColumn === col.id}
                            statuses={statuses}
                            users={users}
                            onAssignLead={onAssignLead}
                            onOpenLead={onOpenLead}
                            onChangeLeadStatus={onChangeLeadStatus}
                            onOpenSchedule={onOpenSchedule}
                            onAddLead={onAddLead}
                            onEditColumn={onEditColumn}
                            onDeleteColumn={onDeleteColumn}
                            onSetEntryColumn={onSetEntryColumn}
                            boardActive={board.active}
                            focusedLead={focusedLead}
                            previewHere={preview?.columnId === col.id}
                            previewBefore={preview?.before ?? null}
                        />
                    ))}
                </SortableContext>

                {canManageBoards && (
                    <MDButton
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={onAddColumn}
                        disabled={!board.active}
                        sx={addColumnButtonSx(cc, controller.darkMode)}
                    >
                        {t('crmKanban.adicionarColuna')}
                    </MDButton>
                )}
            </Box>

            <CrmDropActions visible={Boolean(activeLead) && !activeColumn} canMove={canMoveToBoard} />

            {/* Sem Overlay no arraste de coluna: o dnd-kit suprime o transform e ela teleporta. */}
            {!activeColumn && (
                <DragOverlay>
                    {activeLead && (
                        <CrmLeadCard lead={activeLead} onOpen={() => {}} boardActive={board.active} />
                    )}
                </DragOverlay>
            )}
        </DndContext>
    );
}
